import './assets/main.scss'
import logger from './logger.ts'
import { createApp, createVNode, render, h } from 'vue'
import type { Component, App } from 'vue'
import type { IAOEConfig } from './types/AoeTypes'
import { createPinia } from 'pinia'
import { createI18n } from 'vue-i18n'
import '@progress/kendo-theme-default/dist/all.css'
import { Editor } from '@progress/kendo-vue-editor'
import { InitializeCurrencyFormatter } from './services/currencyFormatter'
import { initAnalytics } from './analytics/index.ts'

const pinia = createPinia()

import EmptyApp from './EmptyApp.vue'
import AoeCartImageButton from './components/AoeCartImageButton.vue'
import AoeAddToCartButton from './components/AoeAddToCartButton.vue'
import AoeSearchButton from './components/AoeSearchButton.vue'
import AoeSearchBar from './components/AoeSearchBar.vue'
import AoeWishlistList from './components/AoeWishlistList.vue'
import AoeWishlistListDetail from './components/AoeWishlistListDetail.vue'
import AoeCartDetail from './components/AoeCartDetail.vue'
import AoeWishlistImageButton from './components/AoeWishlistImageButton.vue'
import AoeCartDrawer from './components/AoeCartDrawer.vue'
import AoeLogoutButton from './components/AoeLogoutButton.vue'
import AoeCheckout from './components/AoeCheckout.vue'
import AoeThankYou from './components/AoeThankYou.vue'
import AoeLoader from './components/AoeLoader.vue'
import AoeProductReviewList from './components/AoeProductReviewList.vue'
import AoeProductReview from './components/AoeProductReview.vue'
import AoeProductReviewStars from './components/AoeProductReviewStars.vue'
import AoeEventList from './components/PWS/AoeEventList.vue'
import AoeEventManagement from './components/PWS/AoeEventManagement.vue'
import AoeRegistration from './components/AoeRegistration.vue'
import AoeVerifyEmail from './components/AoeVerifyEmail.vue'
import AoePartyOrderEntry from './components/PWS/AoePartyOrderEntry.vue'
import AoeImageButton from './components/AoeImageButton.vue'
import AoeSingleLineEntry from './components/Controls/AoeSingleLineEntry.vue'
import AoeCartItemGrid from './components/Controls/AoeCartItemGrid.vue'
import AoeInvoice from './components/AoeInvoice.vue'
import AoeConsultantDetail from './components/AoeConsultantDetail.vue'
import AoeMyStory from './components/AoeMyStory.vue'
import HostEventList from './components/PWS/HostEventList.vue'
import HostDashboard from './components/PWS/HostDashboard.vue'
import CulturePicker from './components/CulturePicker.vue'
import AoeOrderList from './components/PWS/AoeOrderList.vue'
import AoeLeadList from './components/PWS/AoeLeadList.vue'
import CountryPicker from './components/CountryPicker.vue'
import AoeDashboardProgress from './components/PWS/AoeDashboardProgress.vue'
import AoeAccountInfo from './components/PWS/AoeAccountInfo.vue'
import AoeInvoiceStatus from './components/PWS/AoeInvoiceStatus.vue'
import AoeDashboardIncentivesWithLevels from './components/PWS/AoeDashboardIncentivesWithLevels.vue'
import AoeDashboardInvoiceList from './components/PWS/AoeDashboardInvoiceList.vue'
import AoeDashboardOpenParties from './components/PWS/AoeDashboardOpenParties.vue'
import AoeLinkButton from './components/AoeLinkButton.vue'
import AoeHelpButton from './components/AoeHelpButton.vue'
import AoeParcelLab from './components/AoeParcelLab.vue'
import OptInConfirm from './components/OptInConfirm.vue'
import AoeOrderEntry from './components/PWS/AoeOrderEntry.vue'
import CCCommissionsAndTax from './components/ConsultantCorner/CCCommissionsAndTax.vue'
import CCIncentiveTracker from './components/ConsultantCorner/CCIncentiveTracker.vue'
import CCReportDashboard from './components/ConsultantCorner/CCReportDashboard.vue'
import ApplicationMessageList from './application/ApplicationMessageList.vue'
import ContactList from './components/ConsultantCorner/CCMyContacts.vue'
import AoeNewUserWizard from './components/AoeNewUserWizard.vue'
import CCMAContactDetails from './components/ConsultantCorner/CCMAContactDetails.vue'
import CCMAPasswordChange from './components/ConsultantCorner/CCMAPasswordChange.vue'
import CCMyAccount from './components/ConsultantCorner/CCMyAccount.vue'
import AoeReport from './components/ConsultantCorner/CCReport.vue'
import AoeKitSelection from './components/AoeKitSelection.vue'
import AoeKitCartDetail from './components/AoeKitCartDetail.vue'
import AoeKitCheckout from './components/AoeKitCheckout.vue'
import AoeKitRecruiterSelection from './components/AoeKitRecruiterSelection.vue'
import AoeConsultantLocator from './components/AoeConsultantLocator.vue'
import AOERMAs from './components/AOERMAs.vue'
import ContactEdit from './components/ConsultantCorner/CCEditContact.vue'
import HostPortal from './components/HostPortal.vue'
import AoeEventTasks from './components/PWS/AoeEventTasks.vue'
import AoeRMAEntry from './components/AOERMAEntry.vue'
import BusinessDetails from './components/BusinessDetails.vue'
import TeamIncentiveDetails from './components/TeamIncentiveDetails.vue'
import ConsultantActivity from './components/ConsultantActivity.vue'
import AoeLinkEvent from './components/AoeLinkEvent.vue'
import CCTermsAcknowledgement from './components/ConsultantCorner/CCTermsAcknowledgement.vue'

import { GetWithExpiry, SetWithExpiry } from './services/CacheService'
import axios from 'axios'

import { enMessages } from './assets/enMessages'
import { esMessages } from './assets/esMessages'
import { frMessages } from './assets/frMessages'
import { deMessages } from './assets/deMessages'

import { setDateFormatLocale } from './api/Common'
import { loadLocaleData, loadKendoComponentMessages, loadKendoTranslatedMessages } from './utils/kendoHelper.ts'


declare const AOEConfig: IAOEConfig
let i18nLocale = 'en-US'
let i18nfallbackLocale = ['en']
let cacheSeconds = 3600
if (AOEConfig.I18nLocale && AOEConfig.I18nLocale.length > 0) {
  i18nLocale = AOEConfig.I18nLocale
  i18nfallbackLocale = AOEConfig.I18nFallbackLocale
}

setDateFormatLocale(i18nLocale)
if (AOEConfig.CacheSeconds) {
  cacheSeconds = AOEConfig.CacheSeconds
}

const i18n = createI18n({
  legacy: false, // you must set `false`, to use Composition API
})

;(async () => {
  i18n.global.setLocaleMessage('en', enMessages)
  i18n.global.setLocaleMessage('es', esMessages)
  i18n.global.setLocaleMessage('fr', frMessages)
  i18n.global.setLocaleMessage('de', deMessages)
  i18n.global.setLocaleMessage('de-AT', deMessages)
  await setOverrideMessages()

  if (AOEConfig.MessageKeyMode == true) {
    ;(i18n.global as any).missing = (_locale: string, key: string) => key
  }

  i18n.global.locale.value = i18nLocale
  i18n.global.fallbackLocale.value = i18nfallbackLocale

  // Initialize currency formatter with culture from settings
  await InitializeCurrencyFormatter()

  // Load l
  // ocale data for Kendo
  loadLocaleData(i18nLocale)

  // Register Kendo date/time picker popup labels (Today, Cancel, Set, etc.) for all locales
  loadKendoComponentMessages()

  // Register Kendo grid pager and upload labels from the (overridden) i18n messages for the active locale
  loadKendoTranslatedMessages(i18nLocale, (key, list) => (list ? i18n.global.t(key, list) : i18n.global.t(key)))

  //create fake mount point
  const aoeapp = document.createElement('div')
  aoeapp.id = 'aoeapp'
  aoeapp.style.display = 'none'
  document.body.appendChild(aoeapp)
  const app = createApp(EmptyApp)
  app.use(logger)
  app.use(pinia)
  app.use(i18n)

  // Register Kendo Editor globally
  app.component('KendoEditor', Editor)

  // Initialize Matomo Tag Manager.
  // When the host has already loaded the container in the <head>
  // (AOEConfig.MatomoLoadedInHead), don't inject it again — just enable tracking.
  initAnalytics(AOEConfig.MatomoContainerUrl || '', AOEConfig.MatomoLoadedInHead === true)

  app.mount('#aoeapp')

  mountTag(app, 'Aoe-Cart-Image-Button', AoeCartImageButton)
  mountTag(app, 'Aoe-Add-To-Cart-Button', AoeAddToCartButton)
  mountTag(app, 'Aoe-Search-Button', AoeSearchButton)
  mountTag(app, 'Aoe-Search-Bar', AoeSearchBar)
  mountTag(app, 'Aoe-WishList', AoeWishlistList)
  mountTag(app, 'Aoe-WishList-Detail', AoeWishlistListDetail)
  mountTag(app, 'Aoe-Cart-Detail', AoeCartDetail)
  mountTag(app, 'Aoe-Wishlist-Button', AoeWishlistImageButton)
  mountTag(app, 'Aoe-Cart-Drawer', AoeCartDrawer)
  mountTag(app, 'Aoe-Logout-Button', AoeLogoutButton)
  mountTag(app, 'Aoe-Checkout', AoeCheckout)
  mountTag(app, 'Aoe-Thank-You', AoeThankYou)
  mountTag(app, 'Aoe-Loader', AoeLoader)
  mountTag(app, 'Aoe-Product-Review-List', AoeProductReviewList)
  mountTag(app, 'Aoe-Product-Review', AoeProductReview)
  mountTag(app, 'Aoe-Product-Review-Stars', AoeProductReviewStars)
  mountTag(app, 'Aoe-Event-List', AoeEventList)
  mountTag(app, 'Aoe-Event-Management', AoeEventManagement)
  mountTag(app, 'Aoe-Registration', AoeRegistration)
  mountTag(app, 'Aoe-Verify-Email', AoeVerifyEmail)
  mountTag(app, 'Aoe-Party-Order-Entry', AoePartyOrderEntry)
  mountTag(app, 'Aoe-Image-Button', AoeImageButton)
  mountTag(app, 'Aoe-Single-Line-Entry', AoeSingleLineEntry)
  mountTag(app, 'Aoe-Cart-Item-Grid', AoeCartItemGrid)
  mountTag(app, 'Aoe-Invoice', AoeInvoice)
  mountTag(app, 'Aoe-Invoice-Status', AoeInvoiceStatus)
  mountTag(app, 'Aoe-Consultant-Detail', AoeConsultantDetail)
  mountTag(app, 'Aoe-My-Story', AoeMyStory)
  mountTag(app, 'hosteventlist', HostEventList)
  mountTag(app, 'hostdashboard', HostDashboard)
  mountTag(app, 'Culture-Picker', CulturePicker)
  mountTag(app, 'Aoe-Order-List', AoeOrderList)
  mountTag(app, 'Aoe-Lead-List', AoeLeadList)
  mountTag(app, 'Country-Picker', CountryPicker)
  mountTag(app, 'AOEDashboardProgress', AoeDashboardProgress)
  mountTag(app, 'AOEAccountInfo', AoeAccountInfo)
  mountTag(app, 'AOEDashboardInvoiceList', AoeDashboardInvoiceList)
  mountTag(app, 'AOEDashboardOpenParties', AoeDashboardOpenParties)
  mountTag(app, 'AoeDashboardIncentivesWithLevels', AoeDashboardIncentivesWithLevels)
  mountTag(app, 'Aoe-Link-Button', AoeLinkButton)
  mountTag(app, 'Aoe-Help-Button', AoeHelpButton)
  mountTag(app, 'Aoe-Parcel-Lab', AoeParcelLab)
  mountTag(app, 'Aoe-Logout-Button', AoeLogoutButton)
  mountTag(app, 'OptInConfirm', OptInConfirm)
  mountTag(app, 'Aoe-Order-Entry', AoeOrderEntry)
  mountTag(app, 'Aoe-Commissions', CCCommissionsAndTax)
  mountTag(app, 'Aoe-Incentive-Tracker', CCIncentiveTracker)
  mountTag(app, 'Aoe-Report-Dashboard', CCReportDashboard)
  mountTag(app, 'Application-Message-List', ApplicationMessageList)
  mountTag(app, 'Contact-List', ContactList)
  mountTag(app, 'Contact-Edit', ContactEdit)
  mountTag(app, 'Aoe-New-User-Wizard', AoeNewUserWizard)
  mountTag(app, 'Contact-Details', CCMAContactDetails)
  mountTag(app, 'Password-Change', CCMAPasswordChange)
  mountTag(app, 'My-Account', CCMyAccount)
  mountTag(app, 'Aoe-Report', AoeReport)
  mountTag(app, 'Aoe-Report', AoeReport)
  mountTag(app, 'Aoe-Kit-Selection', AoeKitSelection)
  mountTag(app, 'Aoe-Kit-Cart-Detail', AoeKitCartDetail)
  mountTag(app, 'Aoe-Kit-Checkout', AoeKitCheckout)
  mountTag(app, 'Aoe-Kit-Recruiter-Selection', AoeKitRecruiterSelection)
  mountTag(app, 'Aoe-Consultant-Locator', AoeConsultantLocator)
  mountTag(app, 'Aoe-RMAs', AOERMAs)
  mountTag(app, 'Host-Portal', HostPortal)
    mountTag(app, 'Aoe-Event-Tasks', AoeEventTasks)
    mountTag(app, 'Aoe-RMA-Entry', AoeRMAEntry)
    mountTag(app, 'Business-Details', BusinessDetails)
    mountTag(app, 'Team-Incentive-Details', TeamIncentiveDetails)
    mountTag(app, 'Consultant-Activity', ConsultantActivity)
    mountTag(app, 'Aoe-Link-Event', AoeLinkEvent)
    mountTag(app, 'Terms-Acknowledgement', CCTermsAcknowledgement)
})()

function mountTag(app: App<Element>, tag: string, component: Component) {
  const elements = document.querySelectorAll(tag)
  console.log('tag:', tag, 'elements:', elements.length)
  elements.forEach(elem => {
    const props = [...elem.attributes]
      .filter(attr => attr.name[0] === ':')
      .reduce(
        (props, attr) => {
          const propertyName = attr.name.slice(1)
          // console.log('property', propertyName, attr.value)
          props[propertyName] = attr.value
          return props
        },
        {} as Record<string, string>,
      )
    mountComponent(app, elem, component, props)

    // move the component's DOM before the mount element
    ;[...elem.children].forEach(child =>
      elem.parentNode!.insertBefore(child, elem),
    )

    // remove the mount element not to clutter the page
    elem.remove()
  })
}

function mountComponent(
  app: App<Element>,
  elem: Element,
  component: Component,
  props: Record<string, string>,
) {
  const slots: Record<string, unknown> = {}

  const templates = elem.getElementsByTagName('template')
  if (templates && templates.length > 0) {
    for (let i = 0; i < templates.length; i++) {
      const template = templates[i]
      const slotName = [...template.attributes]
        .find(attr => attr.name.startsWith('v-slot'))
        ?.name.slice(7)

      if (slotName) {
        const slotContent = template.innerHTML
        slots[slotName] = () => h('div', { innerHTML: slotContent })
      }
    }
  }

  const divisions = elem.getElementsByTagName('div')
  if (divisions && divisions.length > 0) {
    for (let i = 0; i < divisions.length; i++) {
      const division = divisions[i]
      const slotName = division.dataset.slot
      if (slotName) {
        const slotContent = division.innerHTML
        slots[slotName] = () => h('div', { innerHTML: slotContent })
      }
    }
  }

  const vNode = createVNode(component, props, slots)
  ;[...elem.children].forEach(child => {
    child.remove()
  })
  vNode.appContext = app._context
  render(vNode, elem)
  return vNode.component
}

async function setOverrideMessages(): Promise<void> {
  if (
    !AOEConfig.MessageFileRootUrl ||
    AOEConfig.MessageFileRootUrl.length == 0
  ) {
    return
  }
  const messageFileUrl =
    AOEConfig.MessageFileRootUrl + '/aoe.' + i18nLocale.toLowerCase() + '.json'

  const cachedData = GetWithExpiry(messageFileUrl)
  if (cachedData) {
    logger.debug('getOverrideMessages: using cached data')
    ;(i18n.global as any).mergeLocaleMessage(i18nLocale, cachedData)
    return
  }

  try {
    const response = await axios.get(messageFileUrl + '?t=' + new Date().getTime())
    logger.debug('getOverrideMessages: response', response)
    const messages = response.data
    await SetWithExpiry(messageFileUrl, messages, cacheSeconds)
    logger.debug('getOverrideMessages: loaded messages from', messageFileUrl)
    ;(i18n.global as any).mergeLocaleMessage(i18nLocale, messages)
    return
  } catch {
    logger.error(
      'getOverrideMessages: failed to load messages from',
      messageFileUrl,
    )
    await SetWithExpiry(messageFileUrl, {}, cacheSeconds)
    return
  }
}

// if (app != undefined) {
//   const components: Record<string, () => Promise<any>> = import.meta.glob(
//     '@/components**/*.vue',
//   )
//   for (const path in components) {
//     // const tag = path
//     //   .match(/([^/]+).vue/)?.[1]
//     //   .split(/(?=[A-Z])/g)
//     //   .join('-')
//     // if (tag == undefined) continue
//     console.log('path=', path)
//   }
// }

// if (app != undefined) {
//   const components: Record<string, () => Promise<any>> = import.meta.glob(
//     '@/components**/*.vue',
//   )

//   for (const path in components) {
//     const tag = path
//       .match(/([^/]+).vue/)?.[1]
//       .split(/(?=[A-Z])/g)
//       .join('-')
//     if (tag == undefined) continue
//     console.log('tag=', tag)

//     // components[path]().then(module => {
//     //   const component = module.default
//     //   console.log('component=', component)
//     //   app.component(tag, component)
//     //   document.querySelectorAll(tag).forEach(elem => {
//     //     console.log(elem.attributes)
//     //     const props = [...elem.attributes]
//     //       .filter(attr => attr.name[0] === ':')
//     //       .reduce(
//     //         (props, attr) => {
//     //           const propertyName = attr.name.slice(1)
//     //           console.log('property', propertyName, attr.value)
//     //           props[propertyName] = attr.value
//     //           return props
//     //         },
//     //         {} as Record<string, string>,
//     //       )
//     //     mountComponent(app, elem, component, props)

//     //     // move the component's DOM before the mount element
//     //     ;[...elem.children].forEach(child =>
//     //       elem.parentNode!.insertBefore(child, elem),
//     //     )

//     //     // remove the mount element not to clutter the page
//     //     elem.remove()
//     //   })
//     // })

//     // document.querySelectorAll(tag).forEach(elem => {
//     //   console.log(elem.attributes)
//     //   const props = [...elem.attributes]
//     //     .filter(attr => attr.name[0] === ':')
//     //     .reduce(
//     //       (props, attr) => {
//     //         const propertyName = attr.name.slice(1)
//     //         console.log('property', propertyName, attr.value)
//     //         props[propertyName] = attr.value
//     //         return props
//     //       },
//     //       {} as Record<string, string>,
//     //     )
//     //   mountComponent(app, elem, component, props)

//     //   // move the component's DOM before the mount element
//     //   ;[...elem.children].forEach(child =>
//     //     elem.parentNode!.insertBefore(child, elem),
//     //   )

//     //   // remove the mount element not to clutter the page
//     //   elem.remove()
//     // })
//   }
// }

// function mountComponent(
//   app: App<Element>,
//   elem: Element,
//   component: Component,
//   props: Record<string, string>,
// ) {
//   const vNode = createVNode(component, props)
//   vNode.appContext = app._context
//   render(vNode, elem)
//   return vNode.component
// }
